import { Link } from "react-router-dom";
import {
    FileText,
    BarChart3,
    FileCheck,
    ArrowRight,
    ChevronRight,
} from "lucide-react";
import SEO from "../components/SEO";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";

const prestations = [
    {
        icon: FileText,
        title: "Tenue de comptabilité courante",
        desc: "Saisie des pièces, suivi des comptes, rapprochements bancaires. Vous disposez d'une comptabilité à jour à tout moment.",
    },
    {
        icon: BarChart3,
        title: "Bouclement annuel",
        desc: "Établissement des comptes annuels selon les normes du Code des obligations suisse. Préparation complète pour l'assemblée générale ou la déclaration fiscale.",
    },
    {
        icon: FileCheck,
        title: "États financiers",
        desc: "Bilan, compte de résultat, annexe aux comptes. Des documents clairs, conformes et exploitables par vos partenaires financiers.",
    },
];

const etapes = [
    {
        num: "01",
        title: "Premier entretien gratuit",
        desc: "Pour comprendre votre activité et vos besoins.",
    },
    {
        num: "02",
        title: "Reprise de votre dossier",
        desc: "Mise en place d'une organisation claire des pièces.",
    },
    {
        num: "03",
        title: "Tenue régulière et bouclement",
        desc: "Suivi continu, point de situation en cours d'année et bouclement annuel.",
    },
];

const faqs = [
    {
        q: "Un indépendant doit-il tenir une comptabilité en Suisse ?",
        a: "Oui. Les entreprises inscrites au registre du commerce tiennent une comptabilité complète. Les indépendants non inscrits dont le chiffre d'affaires reste sous le seuil prévu par le Code des obligations peuvent tenir une comptabilité simplifiée, basée sur les recettes, les dépenses et l'état du patrimoine. Fiduciaire Bleich vous indique le régime qui s'applique à votre situation.",
    },
    {
        q: "Quelle norme comptable s'applique à une PME genevoise ?",
        a: "La plupart des PME établissent leurs comptes selon le Code des obligations suisse. Certaines structures plus grandes appliquent des normes complémentaires. Fiduciaire Bleich établit vos comptes dans le cadre légal adapté à votre taille.",
    },
    {
        q: "Combien coûte la tenue de comptabilité par une fiduciaire à Genève ?",
        a: "Le tarif dépend du volume de pièces, de la fréquence de traitement et de la complexité de votre activité. Fiduciaire Bleich établit un devis clair après un premier entretien gratuit.",
    },
    {
        q: "Peut-on changer de fiduciaire en cours d'année ?",
        a: "Oui, à tout moment. La reprise du dossier s'organise à partir de vos derniers comptes et des pièces de l'exercice en cours.",
    },
];

const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Comptabilité à Genève",
    serviceType: "Tenue de comptabilité et établissement des comptes annuels",
    provider: {
        "@type": "AccountingService",
        name: "Fiduciaire Bleich Sàrl",
        url: "https://fiduciaire-bleich.ch",
    },
    areaServed: {
        "@type": "AdministrativeArea",
        name: "Canton de Genève",
    },
    url: "https://fiduciaire-bleich.ch/comptabilite",
};

const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        {
            "@type": "ListItem",
            position: 1,
            name: "Accueil",
            item: "https://fiduciaire-bleich.ch/",
        },
        {
            "@type": "ListItem",
            position: 2,
            name: "Nos services",
            item: "https://fiduciaire-bleich.ch/",
        },
        {
            "@type": "ListItem",
            position: 3,
            name: "Comptabilité",
            item: "https://fiduciaire-bleich.ch/comptabilite",
        },
    ],
};

const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
        },
    })),
};

const Comptabilite = () => {
    return (
        <>
            <SEO
                title="Comptabilité à Genève | Fiduciaire Bleich"
                description="Fiduciaire Bleich assure la tenue de comptabilité de PME, indépendants et sociétés à Genève depuis 2004. Bouclement annuel, états financiers, norme CO. Premier entretien gratuit."
                canonical="https://fiduciaire-bleich.ch/comptabilite"
            />

            {/* JSON-LD : Service */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceJsonLd) }}
            />
            {/* JSON-LD : BreadcrumbList */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
            />
            {/* JSON-LD : FAQPage */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
            />

            {/* HERO */}
            <section
                data-testid="comptabilite-hero"
                className="relative bg-white overflow-hidden"
            >
                <div
                    aria-hidden="true"
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, #2C3E45 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />
                <div className="container-prose relative py-16 lg:py-24">
                    {/* Breadcrumb */}
                    <nav
                        aria-label="Fil d'Ariane"
                        data-testid="comptabilite-breadcrumb"
                        className="flex items-center gap-2 text-xs text-[#5C6B70] mb-10"
                    >
                        <Link to="/" className="hover:text-[#1C9CAC] transition-colors">
                            Accueil
                        </Link>
                        <ChevronRight size={12} strokeWidth={1.75} />
                        <span>Nos services</span>
                        <ChevronRight size={12} strokeWidth={1.75} />
                        <span className="text-[#2C3E45]">Comptabilité</span>
                    </nav>

                    <div className="max-w-3xl animate-fade-up">
                        <div className="inline-flex items-center gap-2 mb-8 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Nos services
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#2C3E45] leading-[1.05] tracking-tight">
                            Comptabilité à <span className="italic text-[#1C9CAC]">Genève.</span>
                        </h1>
                        <p className="mt-6 font-serif italic text-xl sm:text-2xl text-[#5C6B70]">
                            Une tenue de livres rigoureuse, année après année.
                        </p>
                        <p className="mt-8 text-lg text-[#5C6B70] max-w-2xl leading-relaxed">
                            La comptabilité est le socle de toute gestion saine. Elle conditionne
                            vos décisions, vos obligations fiscales et votre image auprès des
                            banques et des autorités. Fiduciaire Bleich prend en charge
                            l'intégralité de votre comptabilité, de la saisie courante au
                            bouclement annuel, avec la même rigueur depuis 2004.
                        </p>
                    </div>
                </div>
            </section>

            {/* PRESTATIONS */}
            <section
                data-testid="comptabilite-prestations"
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Nos prestations
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Ce que <span className="italic">nous faisons.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB]">
                        {prestations.map(({ icon: Icon, title, desc }, idx) => (
                            <div
                                key={title}
                                data-testid={`prestation-card-${idx}`}
                                className="bg-white p-8 lg:p-10 flex flex-col gap-4 group hover:bg-[#FAFAF7] transition-colors"
                            >
                                <div className="w-11 h-11 flex items-center justify-center border border-[#1C9CAC]/30 text-[#1C9CAC] group-hover:bg-[#1C9CAC] group-hover:text-white transition-colors">
                                    <Icon size={20} strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-2xl text-[#2C3E45] mt-2">
                                    {title}
                                </h3>
                                <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                    {desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* COMMENT ÇA SE PASSE */}
            <section
                data-testid="comptabilite-process"
                className="bg-white py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Comment ça se passe
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Une mise en route <span className="italic">simple.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
                        {etapes.map((e, idx) => (
                            <div
                                key={e.num}
                                data-testid={`comptabilite-etape-${idx}`}
                                className="border-t border-[#1C9CAC] pt-8"
                            >
                                <span className="font-serif text-3xl text-[#1C9CAC] block mb-3">
                                    {e.num}
                                </span>
                                <h3 className="font-serif text-2xl text-[#2C3E45] mb-3 leading-snug">
                                    {e.title}
                                </h3>
                                <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                    {e.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* POURQUOI FIDUCIAIRE BLEICH */}
            <section
                data-testid="comptabilite-pourquoi"
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-3xl mb-16">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Pourquoi Fiduciaire Bleich
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Un interlocuteur comptable
                            <br />
                            de <span className="italic">confiance à Genève.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
                        <div
                            data-testid="comptabilite-pourquoi-col-0"
                            className="border-t border-[#1C9CAC] pt-8"
                        >
                            <h3 className="font-serif text-2xl text-[#2C3E45] mb-4 leading-snug">
                                Un suivi continu
                            </h3>
                            <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                Votre dossier est suivi par la même personne d'une année sur
                                l'autre. Pas de turn-over, pas de perte d'information.
                            </p>
                        </div>
                        <div
                            data-testid="comptabilite-pourquoi-col-1"
                            className="border-t border-[#1C9CAC] pt-8"
                        >
                            <h3 className="font-serif text-2xl text-[#2C3E45] mb-4 leading-snug">
                                Une vision d'ensemble
                            </h3>
                            <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                La comptabilité est liée à la{" "}
                                <Link
                                    to="/fiscalite"
                                    data-testid="link-fiscalite"
                                    className="text-[#1C9CAC] hover:underline"
                                >
                                    fiscalité
                                </Link>
                                , à la{" "}
                                <Link
                                    to="/tva"
                                    data-testid="link-tva"
                                    className="text-[#1C9CAC] hover:underline"
                                >
                                    TVA
                                </Link>{" "}
                                et aux{" "}
                                <Link
                                    to="/salaires-rh"
                                    data-testid="link-salaires-rh"
                                    className="text-[#1C9CAC] hover:underline"
                                >
                                    salaires
                                </Link>
                                . Fiduciaire Bleich traite tous ces domaines en interne, pour une
                                cohérence totale de votre dossier.
                            </p>
                        </div>
                        <div
                            data-testid="comptabilite-pourquoi-col-2"
                            className="border-t border-[#1C9CAC] pt-8"
                        >
                            <h3 className="font-serif text-2xl text-[#2C3E45] mb-4 leading-snug">
                                Présents à Genève depuis 2004
                            </h3>
                            <p className="text-[#5C6B70] leading-relaxed text-[15px]">
                                Fiduciaire Bleich connaît le tissu économique local et les
                                exigences des autorités fiscales genevoises.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                data-testid="comptabilite-faq"
                className="bg-white py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl mb-12">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Questions fréquentes
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Questions fréquentes
                            <br />
                            sur la <span className="italic">comptabilité.</span>
                        </h2>
                    </div>

                    <Accordion
                        type="single"
                        collapsible
                        data-testid="comptabilite-faq-accordion"
                        className="max-w-3xl"
                    >
                        {faqs.map((f, idx) => (
                            <AccordionItem
                                key={idx}
                                value={`faq-${idx}`}
                                data-testid={`comptabilite-faq-item-${idx}`}
                                className="border-b border-[#E5E7EB]"
                            >
                                <AccordionTrigger className="font-serif text-lg sm:text-xl text-[#2C3E45] py-6 hover:no-underline text-left">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-[#5C6B70] leading-relaxed text-[15px] pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* PREMIER CONTACT (CTA) */}
            <section
                data-testid="comptabilite-cta"
                className="bg-[#FAFAF7] py-20 lg:py-28 border-t border-[#E5E7EB]"
            >
                <div className="container-prose">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.25em] text-[#5C6B70]">
                            <span className="w-8 h-px bg-[#1C9CAC]" />
                            Premier contact
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2C3E45] leading-tight">
                            Parlons de votre <span className="italic">comptabilité.</span>
                        </h2>
                        <p className="mt-6 text-lg text-[#5C6B70] leading-relaxed">
                            Un premier entretien gratuit et confidentiel permet d'évaluer comment
                            Fiduciaire Bleich peut prendre en charge votre comptabilité. Sans
                            engagement.
                        </p>
                        <div className="mt-10">
                            <Link
                                to="/contact"
                                data-testid="comptabilite-cta-rdv"
                                className="btn-primary"
                            >
                                Prendre rendez-vous
                                <ArrowRight size={16} strokeWidth={1.75} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Comptabilite;
